// Fonctions utilitaires pour le formulaire d'inscription

// Constantes de validation des fichiers
// Note: La limite est de 7 MB car les fichiers sont convertis en base64 (~+40% de taille)
// ce qui donne ~9.8 MB dans l'API, restant sous la limite serveur de 10 MB
export const MAX_TOTAL_SIZE = 7 * 1024 * 1024; // 7 MB pour les 2 fichiers
export const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

/**
 * Valide la taille totale des fichiers
 */
export function validateFiles(piece1: File | null, piece2: File | null): string | null {
  const size1 = piece1?.size || 0;
  const size2 = piece2?.size || 0;
  const totalSize = size1 + size2;
  
  // Vérifier la taille totale uniquement
  if (totalSize > MAX_TOTAL_SIZE) {
    const size1MB = (size1 / 1024 / 1024).toFixed(2);
    const size2MB = (size2 / 1024 / 1024).toFixed(2);
    const totalMB = (totalSize / 1024 / 1024).toFixed(2);
    
    if (piece1 && piece2) {
      return `La taille totale est trop importante : Pièce 1 (${size1MB} MB) + Pièce 2 (${size2MB} MB) = ${totalMB} MB. Maximum : 7 MB`;
    } else if (piece1) {
      return `Le fichier est trop volumineux (${size1MB} MB). Maximum : 7 MB`;
    } else if (piece2) {
      return `Le fichier est trop volumineux (${size2MB} MB). Maximum : 7 MB`;
    }
  }
  
  return null; // Validation OK
}

/**
 * Valide les types de fichiers
 */
export function validateFileTypes(piece1: File | null, piece2: File | null): string | null {
  if (piece1 && !ALLOWED_FILE_TYPES.includes(piece1.type)) {
    return `Le format du fichier Pièce 1 n'est pas autorisé. Formats acceptés : PDF, JPEG, PNG`;
  }
  
  if (piece2 && !ALLOWED_FILE_TYPES.includes(piece2.type)) {
    return `Le format du fichier Pièce 2 n'est pas autorisé. Formats acceptés : PDF, JPEG, PNG`;
  }
  
  return null;
}

/**
 * Validation complète des fichiers (type + taille)
 */
export function validateFilesComplete(piece1: File | null, piece2: File | null): string | null {
  return validateFileTypes(piece1, piece2) || validateFiles(piece1, piece2);
}

/**
 * Détecte le type MIME à partir des premiers octets du fichier (magic numbers)
 */
const detectMimeType = (base64Data: string): string => {
  try {
    const binaryString = atob(base64Data.substring(0, 20));
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // PNG: 89 50 4E 47
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      return 'image/png';
    }
    
    // JPEG: FF D8 FF
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
      return 'image/jpeg';
    }
    
    // PDF: 25 50 44 46
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      return 'application/pdf';
    }
    
    // DOCX: 50 4B (ZIP signature, DOCX are ZIP files)
    if (bytes[0] === 0x50 && bytes[1] === 0x4B) {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
  } catch (error) {
    console.error("Erreur lors de la détection du type MIME:", error);
  }
  
  return 'application/pdf'; // Par défaut
};

/**
 * Convertit une chaîne base64 en Blob
 */
export const base64ToBlob = (base64: string): Blob => {
  let mimeType = "application/pdf";
  let base64Data = base64;
  
  if (base64.includes(",")) {
    const parts = base64.split(",");
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (mimeMatch) {
      mimeType = mimeMatch[1];
    }
    base64Data = parts[1];
  } else {
    // Si pas de préfixe, détecter le type depuis les magic numbers
    mimeType = detectMimeType(base64Data);
  }
  
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * Convertit un fichier en chaîne base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Construit l'ISBN complet à partir des parties
 */
export const buildISBN = (
  indicatif?: string,
  isbn1?: string,
  isbn2?: string
): string | undefined => {
  if (!isbn1 && !isbn2) {
    return undefined;
  }
  
  const prefix = indicatif || "978";
  return `${prefix}-${isbn1 || ""}-${isbn2 || ""}`;
};

/**
 * Décompose l'ISBN en parties
 */
export const parseISBN = (isbn: string): {
  indicatif: string;
  isbn1: string;
  isbn2: string;
} | null => {
  if (!isbn) {
    return null;
  }
  
  const parts = isbn.split("-");
  return {
    indicatif: parts[0] || "",
    isbn1: parts[1] || "",
    isbn2: parts[2] || "",
  };
};

/**
 * Convertit les fichiers du formulaire au format Fichiers[] avec noms originaux
 * Lance une erreur si les fichiers ne respectent pas les contraintes de taille ou de type
 */
export const convertFilesToFichiersArray = async (
  files?: File[]
): Promise<Array<{ Nom: string; Data: string }>> => {
  const fichiers: Array<{ Nom: string; Data: string }> = [];
  
  if (!files || files.length === 0) {
    return fichiers;
  }

  // Filtrer uniquement les fichiers réels (pas les fichiers préfixés piece1/piece2)
  const realFiles = files.filter(file => !file.name.startsWith("piece1") && !file.name.startsWith("piece2"));
  
  // Validation des fichiers (max 2 fichiers)
  const piece1File = realFiles[0] || null;
  const piece2File = realFiles[1] || null;
  
  const validationError = validateFilesComplete(piece1File, piece2File);
  if (validationError) {
    throw new Error(validationError);
  }
  
  // Convertir chaque fichier en base64 avec son nom original
  for (const file of realFiles) {
    const base64Data = await fileToBase64(file);
    fichiers.push({
      Nom: file.name,
      Data: base64Data
    });
  }
  
  return fichiers;
};

/**
 * Convertit un tableau Fichiers[] de l'API en fichiers pour le formulaire
 */
export const convertFichiersToFiles = (fichiers?: Array<{ Nom: string; Data: string }>): File[] => {
  const files: File[] = [];
  
  if (!fichiers || fichiers.length === 0) {
    return files;
  }
  
  for (const fichier of fichiers) {
    try {
      const blob = base64ToBlob(fichier.Data);
      const file = new File([blob], fichier.Nom, { type: blob.type });
      files.push(file);
    } catch (error) {
      console.error(`Erreur lors de la conversion du fichier ${fichier.Nom}:`, error);
    }
  }
  
  return files;
};
